import React, { useState, useEffect } from 'react';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import API_WELCOME from '../utils/API_WELCOME';
import './ICSCalendarWidget.css';

// Types
interface CalendarEvent {
  uid: string;
  summary: string;
  start: Date;
  end: Date;
  status: string;
  description?: string;
}

interface ICSCalendarWidgetProps {
  propertyId: string;
  propertyName?: string;
  monthsToDisplay?: number;
  showTentative?: boolean;
  propertyLink?: string;
}

// Parse ICS file
const parseICS = (icsText: string): CalendarEvent[] => {
  const events: CalendarEvent[] = [];
  const lines = icsText.split(/\r?\n/);
  let currentEvent: Partial<CalendarEvent> | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line === 'BEGIN:VEVENT') {
      currentEvent = { uid: '', summary: 'Booking', status: 'CONFIRMED' };
    } else if (line === 'END:VEVENT' && currentEvent) {
      if (currentEvent.start && currentEvent.end) {
        events.push(currentEvent as CalendarEvent);
      }
      currentEvent = null;
    } else if (currentEvent) {
      if (line.startsWith('UID:')) {
        currentEvent.uid = line.substring(4);
      } else if (line.startsWith('SUMMARY:')) {
        currentEvent.summary = line.substring(8);
      } else if (line.startsWith('DTSTART')) {
        const dateStr = line.split(':')[1] || line.split(';')[1]?.split(':')[1];
        if (dateStr) currentEvent.start = parseICSDate(dateStr);
      } else if (line.startsWith('DTEND')) {
        const dateStr = line.split(':')[1] || line.split(';')[1]?.split(':')[1];
        if (dateStr) currentEvent.end = parseICSDate(dateStr);
      } else if (line.startsWith('STATUS:')) {
        currentEvent.status = line.substring(7);
      } else if (line.startsWith('DESCRIPTION:')) {
        currentEvent.description = line.substring(12);
      }
    }
  }

  return events;
};

const parseICSDate = (dateStr: string): Date => {
  // Handle YYYYMMDD or YYYYMMDDTHHMMSS format
  const year = parseInt(dateStr.substring(0, 4));
  const month = parseInt(dateStr.substring(4, 6)) - 1;
  const day = parseInt(dateStr.substring(6, 8));
  return new Date(year, month, day);
};

// Main Calendar Component
const ICSCalendarWidget: React.FC<ICSCalendarWidgetProps> = ({
  propertyId,
  propertyName = 'Property',
  monthsToDisplay = 3,
  showTentative = true,
  propertyLink,
}) => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  const monthNames = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];
  const dayNames = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

  useEffect(() => {
    const fetchICSFromBackend = async () => {
      try {
        setLoading(true);
        const response = await API_WELCOME.getCalendarEvents({
          uui: propertyId,
        });
        if (!response.ok) throw new Error('Failed to load events');
        console.info('all good', response);
        setEvents(response.events);
      } catch (err) {
        console.error(err);
        setError('Failed to load events from backend');
      } finally {
        setLoading(false);
      }
    };
    if (propertyId) {
      fetchICSFromBackend();
    }
  }, [propertyId]);

  const goToPrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - monthsToDisplay);
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + monthsToDisplay);
    setCurrentDate(newDate);
  };

  const getEventForDate = (date: Date): CalendarEvent | null => {
    return (
      events.find((event) => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);
        eventStart.setHours(0, 0, 0, 0);
        eventEnd.setHours(0, 0, 0, 0);
        date.setHours(0, 0, 0, 0);
        return date >= eventStart && date < eventEnd;
      }) || null
    );
  };

  const isCheckIn = (date: Date, event: CalendarEvent): boolean => {
    const eventStart = new Date(event.start);
    eventStart.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date.getTime() === eventStart.getTime();
  };

  const isCheckOut = (date: Date, event: CalendarEvent): boolean => {
    const eventEnd = new Date(event.end);
    eventEnd.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date.getTime() === eventEnd.getTime();
  };

  const renderMonth = (monthOffset: number) => {
    const date = new Date(currentDate);
    date.setMonth(date.getMonth() + monthOffset);
    date.setDate(1);

    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = date.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(
        <td key={`empty-${i}`} className="ics-calendar__empty-day"></td>,
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDay = new Date(year, month, day);
      const event = getEventForDate(currentDay);

      let cellClass = 'ics-calendar__day';
      let dayContent = null;

      if (event) {
        const checkIn = isCheckIn(currentDay, event);
        const checkOut = isCheckOut(currentDay, event);

        if (checkIn) {
          cellClass += ' ics-calendar__day--check-in';
          dayContent = (
            <div className="ics-calendar__day-wrap ics-calendar__day-wrap--booked">
              <div className="ics-calendar__day-morning"></div>
              <div className="ics-calendar__day-afternoon ics-calendar__booked-bg"></div>
              <span className="ics-calendar__day-number">{day}</span>
            </div>
          );
        } else if (checkOut) {
          cellClass += ' ics-calendar__day--check-out';
          dayContent = (
            <div className="ics-calendar__day-wrap">
              <div className="ics-calendar__day-morning ics-calendar__booked-bg"></div>
              <div className="ics-calendar__day-afternoon"></div>
              <span className="ics-calendar__day-number">{day}</span>
            </div>
          );
        } else {
          cellClass += ' ics-calendar__day--booked';
          dayContent = (
            <div className="ics-calendar__day-wrap ics-calendar__day-wrap--booked">
              <div className="ics-calendar__day-morning ics-calendar__booked-bg"></div>
              <div className="ics-calendar__day-afternoon ics-calendar__booked-bg"></div>
              <span className="ics-calendar__day-number">{day}</span>
            </div>
          );
        }
      } else {
        dayContent = (
          <div className="ics-calendar__day-wrap ics-calendar__day-wrap--free">
            <div className="ics-calendar__day-morning"></div>
            <div className="ics-calendar__day-afternoon"></div>
            <span className="ics-calendar__day-number">{day}</span>
          </div>
        );
      }

      days.push(
        <td key={day} className={cellClass} title={event?.summary}>
          {dayContent}
        </td>,
      );
    }

    const totalCells = firstDay + daysInMonth;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let i = 0; i < remainingCells; i++) {
      days.push(
        <td key={`empty-end-${i}`} className="ics-calendar__empty-day"></td>,
      );
    }

    const rows = [];
    for (let i = 0; i < days.length; i += 7) {
      rows.push(<tr key={i / 7}>{days.slice(i, i + 7)}</tr>);
    }

    return (
      <table className="ics-calendar__month-table" key={monthOffset}>
        <thead>
          <tr className="ics-calendar__month-header">
            <th colSpan={7}>
              {monthNames[month]} {year}
            </th>
          </tr>
          <tr className="ics-calendar__day-header">
            {dayNames.map((day) => (
              <th key={day}>{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    );
  };

  if (loading) {
    return (
      <div className="ics-calendar">
        <div className="ics-calendar__loading">
          Loading calendar...
          {retryCount > 0 && (
            <div className="ics-calendar__retry-info">
              Retry attempt {retryCount}/{MAX_RETRIES}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ics-calendar">
        <div className="ics-calendar__error">{error}</div>
      </div>
    );
  }

  return (
    <div className="ics-calendar">
      <div className="ics-calendar__nav">
        <button
          className="ics-calendar__nav-button"
          onClick={goToPrevMonth}
          aria-label="Previous months"
        >
          <ChevronLeft />
        </button>
        {propertyLink ? (
          <a
            href={propertyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="ics-calendar__property-name ics-calendar__property-name--link"
          >
            {propertyName}
          </a>
        ) : (
          <span className="ics-calendar__property-name">{propertyName}</span>
        )}
        <button
          className="ics-calendar__nav-button"
          onClick={goToNextMonth}
          aria-label="Next months"
        >
          <ChevronRight />
        </button>
      </div>

      <div className="ics-calendar__months-container">
        {Array.from({ length: monthsToDisplay }, (_, i) => renderMonth(i))}
      </div>

      <div className="ics-calendar__footer">Powered by Welcome2BA</div>
    </div>
  );
};

export default ICSCalendarWidget;
